/**
 * MarsMate — Custom Magnetic Cursor (Desktop only)
 * Trailing dot + ring cursor with magnetic pull on hoverable elements.
 *
 * Dependencies:
 *   framer-motion (already installed)
 *
 * Setup:
 *   1. Add to your root App.js (outside of Routes):
 *      import CustomCursor from './animations/CustomCursor';
 *      <CustomCursor />
 *
 *   2. Add data-cursor attributes to elements for effects:
 *      data-cursor="hover"    — ring scales up (links, cards)
 *      data-cursor="cta"      — ring fills indigo (CTA buttons)
 *      data-cursor="text"     — slim bar cursor (text fields)
 *      data-cursor="drag"     — shows drag indicator (canvas)
 *
 *   3. Hide the system cursor globally:
 *      * { cursor: none !important; }
 *      (or use the provided CSS class `.hide-cursor *`)
 *
 * Note: Automatically disabled on touch devices and when
 *       prefers-reduced-motion is active.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const SPRING_CONFIG = { stiffness: 500, damping: 32, mass: 0.5 };
const DOT_SPRING    = { stiffness: 800, damping: 40, mass: 0.3 };

export default function CustomCursor() {
  const [cursorType, setCursorType] = useState('default'); // default | hover | cta | text | drag | hidden
  const [isVisible, setIsVisible]   = useState(false);

  // Raw mouse position
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  // Smoothed ring position (slower = trailing effect)
  const ringX = useSpring(mouseX, SPRING_CONFIG);
  const ringY = useSpring(mouseY, SPRING_CONFIG);

  // Fast dot position (nearly instant)
  const dotX = useSpring(mouseX, DOT_SPRING);
  const dotY = useSpring(mouseY, DOT_SPRING);

  // Detect touch device
  const isTouch = typeof window !== 'undefined' &&
    window.matchMedia('(hover: none)').matches;

  // Detect reduced motion
  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (isTouch || reducedMotion) return;

    function onMove(e) {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      setIsVisible(true);
    }

    function onLeave() { setIsVisible(false); }
    function onEnter() { setIsVisible(true); }

    function updateCursorType(e) {
      const target = e.target;
      const type = target.closest('[data-cursor]')?.dataset?.cursor;

      if (type) {
        setCursorType(type);
        return;
      }

      const tag = target.tagName?.toLowerCase();
      const isInteractive =
        tag === 'a' || tag === 'button' ||
        target.getAttribute('role') === 'button' ||
        target.closest('a') || target.closest('button') ||
        target.style?.cursor === 'pointer' ||
        window.getComputedStyle(target).cursor === 'pointer';

      setCursorType(isInteractive ? 'hover' : 'default');
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mousemove', updateCursorType);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousemove', updateCursorType);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
    };
  }, [mouseX, mouseY, isTouch, reducedMotion]);

  if (isTouch || reducedMotion) return null;

  // Ring styles per cursor type
  const ringConfig = {
    default: { scale: 1,    borderColor: 'rgba(193, 68, 14,0.6)',  background: 'transparent', size: 32 },
    hover:   { scale: 1.6,  borderColor: 'rgba(193, 68, 14,0.8)',  background: 'rgba(193, 68, 14,0.08)', size: 32 },
    cta:     { scale: 1.4,  borderColor: '#C1440E',               background: 'rgba(193, 68, 14,0.18)', size: 32 },
    text:    { scale: 0.3,  borderColor: '#F1F5F9',               background: '#F1F5F9', size: 32 },
    drag:    { scale: 1.8,  borderColor: 'rgba(245, 166, 35,0.7)',   background: 'rgba(245, 166, 35,0.08)', size: 32 },
    hidden:  { scale: 0,    borderColor: 'transparent',           background: 'transparent', size: 32 },
  };

  const cfg = ringConfig[cursorType] || ringConfig.default;

  return (
    <div
      style={{ pointerEvents: 'none', position: 'fixed', inset: 0, zIndex: 99999 }}
      aria-hidden="true"
    >
      {/* Outer ring — trails cursor */}
      <motion.div
        style={{
          position: 'fixed',
          x: ringX,
          y: ringY,
          translateX: '-50%',
          translateY: '-50%',
          width: cfg.size,
          height: cfg.size,
          borderRadius: '50%',
          border: `1.5px solid ${cfg.borderColor}`,
          background: cfg.background,
          opacity: isVisible ? 1 : 0,
          pointerEvents: 'none',
        }}
        animate={{
          scale: cfg.scale,
          borderColor: cfg.borderColor,
          background: cfg.background,
          opacity: isVisible ? 1 : 0,
        }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      />

      {/* Inner dot — snappy, stays on exact mouse position */}
      <motion.div
        style={{
          position: 'fixed',
          x: dotX,
          y: dotY,
          translateX: '-50%',
          translateY: '-50%',
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: cursorType === 'cta' ? '#C1440E' :
                      cursorType === 'drag' ? '#F5A623' : '#F1F5F9',
          opacity: isVisible && cursorType !== 'text' ? 1 : 0,
          pointerEvents: 'none',
        }}
        transition={{ duration: 0.1 }}
      />

      {/* Drag label */}
      {cursorType === 'drag' && (
        <motion.div
          style={{
            position: 'fixed',
            x: ringX,
            y: ringY,
            translateX: '-50%',
            translateY: '-50%',
            color: '#F5A623',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            pointerEvents: 'none',
            opacity: isVisible ? 1 : 0,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
        >
          DRAG
        </motion.div>
      )}
    </div>
  );
}

/**
 * useMagneticEffect — makes an element magnetically attract the cursor.
 * Attach the returned ref and spread motionProps onto a motion.div.
 *
 * Usage:
 *   const { ref, motionProps } = useMagneticEffect();
 *   <motion.button ref={ref} {...motionProps}>Click me</motion.button>
 */
export function useMagneticEffect(strength = 0.35) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  function onMouseMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * strength);
    y.set((e.clientY - centerY) * strength);
  }

  function onMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return {
    ref,
    motionProps: {
      onMouseMove,
      onMouseLeave,
      style: { x: springX, y: springY },
    },
  };
}
